import { useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import api from '../../../utils/api';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';

function Diseases() {
  const { data: diseases, loading, fetchData } = useApi('/diseases');
  const [modalType, setModalType] = useState(null); // 'add', 'edit', 'view'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    symptoms: [],
    treatments: [{ medication: '', dosage: '', administration: '' }],
    preventiveMeasures: [],
  });

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSymptomAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      setForm({
        ...form,
        symptoms: [...form.symptoms, e.target.value.trim()],
      });
      e.target.value = '';
    }
  };

  const handleSymptomRemove = (index) => {
    setForm({
      ...form,
      symptoms: form.symptoms.filter((_, i) => i !== index),
    });
  };

  const handlePreventiveMeasureAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      setForm({
        ...form,
        preventiveMeasures: [...form.preventiveMeasures, e.target.value.trim()],
      });
      e.target.value = '';
    }
  };

  const handlePreventiveMeasureRemove = (index) => {
    setForm({
      ...form,
      preventiveMeasures: form.preventiveMeasures.filter((_, i) => i !== index),
    });
  };

  const handleTreatmentChange = (index, field, value) => {
    const updatedTreatments = form.treatments.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    );
    setForm({ ...form, treatments: updatedTreatments });
  };

  const addTreatment = () => {
    setForm({
      ...form,
      treatments: [...form.treatments, { medication: '', dosage: '', administration: '' }],
    });
  };

  const removeTreatment = (index) => {
    setForm({
      ...form,
      treatments: form.treatments.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: form.name,
        symptoms: form.symptoms,
        treatments: form.treatments.filter((t) => t.medication || t.dosage || t.administration),
        preventiveMeasures: form.preventiveMeasures,
      };
      if (modalType === 'edit') {
        await api.put(`/diseases/${editingId}`, data);
        toast.success('Disease updated successfully');
      } else {
        await api.post('/diseases', data);
        toast.success('Disease added successfully');
      }
      setModalType(null);
      setEditingId(null);
      setForm({
        name: '',
        symptoms: [],
        treatments: [{ medication: '', dosage: '', administration: '' }],
        preventiveMeasures: [],
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save disease');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this disease?')) {
      try {
        await api.delete(`/diseases/${id}`);
        toast.success('Disease deleted successfully');
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to delete disease');
      }
    }
  };

  const openModal = (type, disease = null) => {
    setModalType(type);
    if (type === 'edit' || type === 'view') {
      setEditingId(disease._id);
      setForm({
        name: disease.name,
        symptoms: disease.symptoms || [],
        treatments: disease.treatments.length
          ? disease.treatments
          : [{ medication: '', dosage: '', administration: '' }],
        preventiveMeasures: disease.preventiveMeasures || [],
      });
    } else {
      setForm({
        name: '',
        symptoms: [],
        treatments: [{ medication: '', dosage: '', administration: '' }],
        preventiveMeasures: [],
      });
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    {
      key: 'symptoms',
      label: 'Symptoms',
      render: (disease) =>
        disease.symptoms.slice(0, 3).join(', ') +
        (disease.symptoms.length > 3 ? '...' : ''),
    },
    {
      key: 'treatments',
      label: 'Treatments',
      render: (disease) =>
        disease.treatments
          .slice(0, 2)
          .map((t) => t.medication)
          .join(', ') + (disease.treatments.length > 2 ? '...' : ''),
    },
  ];

  const actions = [
    {
      label: 'View',
      className: 'text-blue-500 hover:text-blue-700',
      icon: <FaEye />,
      onClick: (disease) => openModal('view', disease),
    },
    {
      label: 'Edit',
      className: 'text-yellow-500 hover:text-yellow-600',
      icon: <FaEdit />,
      onClick: (disease) => openModal('edit', disease),
    },
    {
      label: 'Delete',
      className: 'text-red-500 hover:text-red-600',
      icon: <FaTrash />,
      onClick: (disease) => handleDelete(disease._id),
    },
  ];

  return (
    <div className="">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Disease Management
        </h2>
        <button
          onClick={() => openModal('add')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 shadow-md"
        >
          Add Disease
        </button>
      </div>
      {loading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">
          Loading...
        </div>
      ) : diseases?.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">
          No diseases found.
        </div>
      ) : (
        <div className="card">
          <Table columns={columns} data={diseases || []} actions={actions} />
        </div>
      )}

      {/* Modal for Add/Edit/View */}
      <Modal
        isOpen={modalType !== null}
        onClose={() => {
          setModalType(null);
          setEditingId(null);
          setForm({
            name: '',
            symptoms: [],
            treatments: [{ medication: '', dosage: '', administration: '' }],
            preventiveMeasures: [],
          });
        }}
        title={
          modalType === 'add'
            ? 'Add Disease'
            : modalType === 'edit'
            ? 'Edit Disease'
            : 'View Disease'
        }
        className="max-w-full lg:max-w-4xl mx-4" // Wider modal for horizontal layout
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          {modalType === 'view' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <p className="mt-1 text-gray-900 dark:text-gray-100">{form.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Symptoms
                </label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {form.symptoms.map((symptom, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 max-lg:text-xs"
                    >
                      {symptom}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Treatments
                </label>
                <div className="mt-1 space-y-2">
                  {form.treatments.map((t, index) => (
                    <div key={index} className="text-gray-900 dark:text-gray-100">
                      <p>
                        <strong>Medication:</strong> {t.medication || 'N/A'}
                      </p>
                      <p>
                        <strong>Dosage:</strong> {t.dosage || 'N/A'}
                      </p>
                      <p>
                        <strong>Administration:</strong> {t.administration || 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Preventive Measures
                </label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {form.preventiveMeasures.map((measure, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 max-lg:text-xs"
                    >
                      {measure}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex justify-end sticky bottom-0 bg-white dark:bg-gray-800 pt-4">
                <button
                  onClick={() => setModalType(null)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="lg:col-span-2">
                  <FormInput
                    label="Disease Name"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter disease name (e.g., Avian Flu)"
                  />
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Symptoms
                  </label>
                  <input
                    type="text"
                    onKeyDown={handleSymptomAdd}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Type a symptom and press Enter"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.symptoms.map((symptom, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 max-lg:text-xs"
                      >
                        {symptom}
                        <button
                          type="button"
                          onClick={() => handleSymptomRemove(index)}
                          className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Preventive Measures
                  </label>
                  <input
                    type="text"
                    onKeyDown={handlePreventiveMeasureAdd}
                    className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Type a preventive measure and press Enter"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.preventiveMeasures.map((measure, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 max-lg:text-xs"
                      >
                        {measure}
                        <button
                          type="button"
                          onClick={() => handlePreventiveMeasureRemove(index)}
                          className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-500"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                {form.treatments.map((treatment, index) => (
                  <div key={index} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg relative">
                    <FormInput
                      label="Medication"
                      name={`medication-${index}`}
                      value={treatment.medication}
                      onChange={(e) =>
                        handleTreatmentChange(index, 'medication', e.target.value)
                      }
                      placeholder="e.g., Antiviral"
                    />
                    <FormInput
                      label="Dosage"
                      name={`dosage-${index}`}
                      value={treatment.dosage}
                      onChange={(e) =>
                        handleTreatmentChange(index, 'dosage', e.target.value)
                      }
                      placeholder="e.g., 10mg"
                    />
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Administration
                      </label>
                      <select
                        value={treatment.administration}
                        onChange={(e) =>
                          handleTreatmentChange(index, 'administration', e.target.value)
                        }
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                      >
                        <option value="">Select Administration</option>
                        <option value="Oral">Oral</option>
                        <option value="Injection">Injection</option>
                        <option value="Topical">Topical</option>
                      </select>
                    </div>
                    {form.treatments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTreatment(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                ))}
                <div className="lg:col-span-2">
                  <button
                    type="button"
                    onClick={addTreatment}
                    className="mt-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    + Add Another Treatment
                  </button>
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6 sticky bottom-0 bg-white dark:bg-gray-800 pt-4">
                <button
                  type="button"
                  onClick={() => setModalType(null)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {modalType === 'edit' ? 'Update Disease' : 'Add Disease'}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default Diseases;